import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllMeetingRooms,
  useGetMeetingRoomById,
} from "../../../hooks/common/query";
import { mapToOptions } from "../../../lib/functions";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import useAuthStore from "../../../store/Auth";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import { MeetingRoomsSchema } from "../../../schemas/meetingRoomsSchema";
import MeetingRoomsTable from "../../../components/table/MeetingRoomsTable";
import { PAGE_SIZE } from "../../../constants/common";
import { MeetingRoom } from "../../../hooks/common/interface";
import {
  useAddUpdateMeetingRoom,
  useIsMeetingRoomExists,
} from "../../../hooks/common/mutation";

interface FormData {
  meetingRoomNameEn: string;
  meetingRoomNameAr: string;
  description?: string | null;
  location: string;
  building: string;
}

const initialData = {
  meetingRoomNameEn: "",
  meetingRoomNameAr: "",
  description: "",
  location: "",
  building: "",
};

const MeetingRoomsConfig = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useFormWithSchema(MeetingRoomsSchema);

  const [allMeetingRooms, setAllMeetingRooms] = useState<MeetingRoom[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [selectedMeetingRoomId, setSelectedMeetingRoomId] = useState(0);

  const { mutateAsync: IsMeetingRoomExists } = useIsMeetingRoomExists();
  const { data: meetingRooms } = useGetAllMeetingRooms({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (meetingRooms) {
      setAllMeetingRooms(meetingRooms.data || []);
      setPages(meetingRooms.pagination?.pages || 1);
      setCount(meetingRooms.pagination?.count || 0);
    }
  }, [setPages, meetingRooms]);

  const {
    mutateAsync: AddUpdateMeetingRoom,
    isPending: isAddUpdateMeetingRoomLoading,
  } = useAddUpdateMeetingRoom();

  const user = useAuthStore((state) => state.user);

  const { data: selectedMeetingRoom } = useGetMeetingRoomById({
    encryptedId: selectedMeetingRoomId ? selectedMeetingRoomId.toString() : "",
  });

  const { data: allLocations } = useGetAllLocationsDropdown();

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const selectedLocationId = watch("location");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  useEffect(() => {
    if (selectedMeetingRoom) {
      setValue("meetingRoomNameEn", selectedMeetingRoom.s_MeetingRoomName_En);
      setValue("meetingRoomNameAr", selectedMeetingRoom.s_MeetingRoomName_Ar);
      setValue("description", selectedMeetingRoom.s_Description);
      setValue("location", selectedMeetingRoom.n_LocationID);
      setValue("building", selectedMeetingRoom.n_BuildingID);
    }
  }, [selectedMeetingRoom, setValue]);

  const onClear = useCallback(() => {
    setSelectedMeetingRoomId(0);
    reset(initialData);
  }, [reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const isExistBody = {
          encryptedMeetingRoomID: selectedMeetingRoomId.toString(),
          encryptedLocationID: data.location,
          n_BuildingID: data.building,
          s_MeetingRoomName_En: data.meetingRoomNameEn,
          s_MeetingRoomName_Ar: data.meetingRoomNameAr,
        };
        const { isExists } = await IsMeetingRoomExists(isExistBody);

        if (isExists) {
          toast.error(t("meetingRoomExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error checking if meeting room exists", error);
        return;
      }

      try {
        if (user?.user_id) {
          const body = {
            n_MeetingRoomID: 0,
            s_MeetingRoomName_En: data.meetingRoomNameEn,
            s_MeetingRoomName_Ar: data.meetingRoomNameAr,
            n_LocationID: Number(data.location),
            n_BuildingID: Number(data.building),
            s_Description: data.description,
          };
          if (selectedMeetingRoomId === 0) {
            await AddUpdateMeetingRoom(body);
          } else {
            const updateBody = {
              ...body,
              n_MeetingRoomID: selectedMeetingRoomId,
            };
            await AddUpdateMeetingRoom(updateBody);
          }
        }
        if (selectedMeetingRoomId === 0) {
          toast.success(t("meetingRoomAdded"));
        } else {
          toast.success(t("meetingRoomUpdated"));
        }

        onClear();
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating department", error);
      }
    },
    [
      selectedMeetingRoomId,
      IsMeetingRoomExists,
      t,
      user?.user_id,
      onClear,
      AddUpdateMeetingRoom,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("meetingRoomsConfiguration")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
              <Controller
                name="meetingRoomNameEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("meetingRoomNameEn")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />

              <Controller
                name="meetingRoomNameAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("meetingRoomNameAr")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                    required
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    required
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <TextInput
                      label={t("description")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button isLoading={isAddUpdateMeetingRoomLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <MeetingRoomsTable
            meetingRooms={allMeetingRooms}
            setSelectedMeetingRoomId={setSelectedMeetingRoomId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default MeetingRoomsConfig;
